import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { authApi, tokenManager } from '../../services/api';
import { LayoutDashboard, FileText, Files, Image, Settings, Users, FolderOpen, StickyNote, Gift, ShoppingCart, ClipboardList, LogOut, Globe, Boxes, UserCheck, ClipboardCheck, Briefcase, Building2, ChevronLeft, ChevronRight } from 'lucide-react';
import logoImg from '../../logo.png';

export default function AdminLayout({ children, active }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const location = useLocation();
  const isPosPage = location.pathname === '/admin/pos';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    return saved !== null ? saved === 'true' : isPosPage;
  });
  const [posOpen, setPosOpen] = useState(false);
  const [operationOpen, setOperationOpen] = useState(false);
  const [cmsOpen, setCmsOpen] = useState(false);
  const navRef = useRef(null);

  // Auto-collapse on POS page, restore otherwise
  useEffect(() => {
    if (isPosPage) setCollapsed(true);
  }, [isPosPage]);

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', collapsed);
  }, [collapsed]);

  useEffect(() => {
    const savedPosOpen = localStorage.getItem('sidebar_pos_open');
    const savedOperationOpen = localStorage.getItem('sidebar_operation_open');
    const savedCmsOpen = localStorage.getItem('sidebar_cms_open');

    if (savedPosOpen !== null) setPosOpen(savedPosOpen === 'true');
    if (savedOperationOpen !== null) setOperationOpen(savedOperationOpen === 'true');
    if (savedCmsOpen !== null) setCmsOpen(savedCmsOpen === 'true');
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebar_pos_open', posOpen);
  }, [posOpen]);

  useEffect(() => {
    localStorage.setItem('sidebar_operation_open', operationOpen);
  }, [operationOpen]);

  useEffect(() => {
    localStorage.setItem('sidebar_cms_open', cmsOpen);
  }, [cmsOpen]);

  useEffect(() => {
    const node = navRef.current;
    if (!node) return;

    const savedScroll = sessionStorage.getItem('admin-sidebar-scroll');
    if (savedScroll) {
      node.scrollTop = Number(savedScroll);
    }

    const handleScroll = () => {
      sessionStorage.setItem('admin-sidebar-scroll', String(node.scrollTop));
    };

    node.addEventListener('scroll', handleScroll, { passive: true });
    return () => node.removeEventListener('scroll', handleScroll);
  }, [collapsed, sidebarOpen]);

  useEffect(() => {
    authApi.me().then(res => setUser(res.data.data)).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await authApi.logout().catch(() => {});
    tokenManager.clear();
    navigate('/admin/login');
  };

  const links = React.useMemo(() => [
    { to: '/admin/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
    { to: '/admin/pos', label: 'POS', Icon: ShoppingCart, highlight: true },
    {
      type: 'group',
      key: 'pos',
      label: 'Product',
      Icon: FolderOpen,
      children: [
        { to: '/admin/categories', label: 'Kategori', Icon: FolderOpen },
        { to: '/admin/layanan', label: 'Layanan', Icon: StickyNote },
        { to: '/admin/packages', label: 'Paket', Icon: Gift },
        { to: '/admin/orders', label: 'Pesanan', Icon: ClipboardList },
      ],
    },
    {
      type: 'group',
      key: 'operation',
      label: 'Operation',
      Icon: Boxes,
      children: [
        { to: '/admin/operation-workers', label: 'Pekerja Operasi', Icon: UserCheck },
        { to: '/admin/operation-inventory', label: 'Inventaris', Icon: Boxes },
        { to: '/admin/operation-categories', label: 'Kategori Inventaris', Icon: FolderOpen },
        { to: '/admin/operation-assignments', label: 'Assignment', Icon: ClipboardCheck },
        { to: '/admin/operation-positions', label: 'Posisi', Icon: Briefcase },
        { to: '/admin/operation-departments', label: 'Departemen', Icon: Building2 },
      ],
    },
    {
      type: 'group',
      key: 'cms',
      label: 'CMS',
      Icon: FileText,
      children: [
        { to: '/admin/pages', label: 'Halaman', Icon: FileText },
        { to: '/admin/sections', label: 'Seksi', Icon: Files },
        { to: '/admin/posts', label: 'Blog', Icon: StickyNote },
        { to: '/admin/media', label: 'Media', Icon: Image },
        { to: '/admin/settings', label: 'Pengaturan', Icon: Settings },
        { to: '/admin/users', label: 'Pengguna', Icon: Users },
      ],
    },
  ], []);

  const handleToggleCollapse = () => {
    setCollapsed((value) => !value);
  };

  const sidebarFontStyle = {
    fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif',
  };

  const renderNavItem = (link, nested = false) => {
    const isActive = active === link.label;
    const baseClassName = `flex items-center rounded-lg border transition-all duration-200 ${collapsed ? 'justify-center px-2.5 py-2.5' : nested ? 'px-3 py-2.5' : 'px-3 py-3'} ${isActive ? 'border-cyan-400/40 bg-gradient-to-r from-cyan-500/15 to-blue-500/10 text-cyan-300 shadow-lg shadow-cyan-500/10' : link.highlight ? 'border-cyan-400/20 bg-cyan-500/10 text-cyan-300 hover:border-cyan-400/40 hover:bg-cyan-500/15 hover:shadow-lg hover:shadow-cyan-500/10' : 'border-blue-700/30 text-blue-200/70 hover:border-blue-600/50 hover:bg-blue-700/20 hover:text-blue-200 hover:shadow-md hover:shadow-blue-700/20'}`;

    return (
      <Link key={link.to} to={link.to}
        className={baseClassName}
        onClick={() => { setSidebarOpen(false); if (isPosPage && link.label === 'POS') setCollapsed(true); }}
        title={collapsed ? link.label : undefined}
      >
        {!collapsed && <span className={`leading-none tracking-[0.01em] ${nested ? 'text-[12px] font-medium' : 'text-[13px] font-semibold'}`}>{link.label}</span>}
      </Link>
    );
  };

  const SidebarContent = () => (
    <div className="flex h-full min-h-0 flex-col bg-gradient-to-b from-slate-900 via-slate-800 to-blue-900" style={sidebarFontStyle}>
      <div className={`flex items-center border-b border-blue-800/40 flex-shrink-0 ${collapsed ? 'justify-center px-2 py-4' : 'justify-between px-4 py-4'}`}>
        {!collapsed ? (
          <div className="flex items-center gap-3 min-w-0">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-blue-200/60">Management</p>
              <p className="text-sm font-bold text-white truncate">Klinnovac</p>
            </div>
          </div>
        ) : null}
        <button onClick={handleToggleCollapse} className="p-1.5 rounded-lg hover:bg-blue-800/30 text-blue-300/60 hover:text-blue-300 transition-colors flex-shrink-0" title={collapsed ? 'Buka sidebar' : 'Tutup sidebar'}>
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>
      <div ref={navRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden" style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'thin', scrollbarGutter: 'stable' }}>
        <nav className="p-3 pb-4 space-y-3" style={{ minHeight: '100%' }}>
          {!collapsed && (
            <div className="px-3 pb-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-blue-300/50">Menu utama</p>
            </div>
          )}
          {links.map((link) => {
            if (link.type === 'group') {
              const isActiveChild = link.children.some((child) => active === child.label);
              const isOpen = link.key === 'pos' ? posOpen : link.key === 'operation' ? operationOpen : cmsOpen;
              const toggleOpen = link.key === 'pos'
                ? () => setPosOpen((value) => !value)
                : link.key === 'operation'
                  ? () => setOperationOpen((value) => !value)
                  : () => setCmsOpen((value) => !value);

              return (
                <div key={link.label} className="rounded-lg border border-blue-700/30 bg-blue-900/20 backdrop-blur-sm p-2 shadow-md shadow-blue-900/20">
                  {!collapsed ? (
                    <>
                      <button
                        type="button"
                        onClick={toggleOpen}
                        className={`flex w-full items-center justify-between rounded-lg px-3 py-3 text-[13px] font-semibold tracking-[0.01em] transition-all ${isActiveChild ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400/30' : 'text-blue-200/70 hover:bg-blue-800/30 hover:text-blue-200 hover:border hover:border-blue-600/40'}`}
                      >
                        <span className="leading-none">{link.label}</span>
                        <ChevronRight size={14} className={`transition-transform text-blue-300/60 ${isOpen ? 'rotate-90' : ''}`} />
                      </button>
                      {isOpen && (
                        <div className="mt-2 space-y-2 border-l border-blue-600/30 pl-3 ml-3 py-2">
                          {link.children.map((child) => (
                            <div key={child.to} className="mt-2 first:mt-0">
                              {renderNavItem(child, true)}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        if (link.key === 'pos') setPosOpen(true);
                        else if (link.key === 'operation') setOperationOpen(true);
                        else setCmsOpen(true);
                      }}
                      className="flex w-full items-center justify-center rounded-lg px-2.5 py-2.5 text-blue-300/70 hover:bg-blue-800/30 hover:text-blue-200 transition-colors font-semibold text-xs"
                      title={link.label}
                    >
                      {link.label.charAt(0)}
                    </button>
                  )}
                </div>
              );
            }

            return renderNavItem(link);
          })}
        </nav>
      </div>
      {!collapsed && (
        <div className="flex-shrink-0 border-t border-blue-800/40 bg-blue-900/30 backdrop-blur-sm px-3 py-3">
          <div className="rounded-2xl border border-cyan-400/30 bg-gradient-to-br from-cyan-500/10 via-blue-700/10 to-blue-900/10 p-3 shadow-lg shadow-cyan-500/5">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-cyan-500/30 text-cyan-300 font-bold text-[11px] flex-shrink-0">{user?.name?.charAt(0)}</div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-white truncate">{user?.name}</p>
                <p className="text-[10px] text-blue-200/50">Admin panel aktif</p>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button onClick={handleLogout} className="flex-1 rounded-lg border border-red-400/40 bg-red-500/10 px-2.5 py-1.5 text-[11px] font-medium text-red-300 hover:bg-red-500/20 hover:border-red-400/60 transition-all" title="Keluar">Keluar</button>
              <Link to="/" target="_blank" className="flex h-8 w-8 items-center justify-center rounded-lg border border-blue-600/40 bg-blue-700/20 text-blue-300/70 hover:text-cyan-300 hover:bg-blue-700/40 hover:border-blue-500/60 transition-all" title="Lihat Website"><Globe size={14} /></Link>
            </div>
          </div>
        </div>
      )}
      {collapsed && (
        <div className="flex-shrink-0 border-t border-blue-800/40 bg-blue-900/30 backdrop-blur-sm flex flex-col items-center gap-2 py-3">
          <button onClick={handleLogout} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400/70 hover:text-red-300 transition-colors" title="Keluar"><LogOut size={14} /></button>
          <Link to="/" target="_blank" className="p-1.5 rounded-lg hover:bg-blue-700/40 text-blue-300/70 hover:text-cyan-300 transition-colors" title="Lihat Website"><Globe size={14} /></Link>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex overflow-hidden">
      <aside className={`hidden lg:flex flex-col fixed inset-y-0 left-0 z-20 bg-gradient-to-b from-slate-900 via-slate-800 to-blue-900 border-r border-blue-800/40 h-screen overflow-hidden transition-all duration-200 ${collapsed ? 'w-14' : 'w-52'}`} style={{ overscrollBehavior: 'contain' }}>
        <SidebarContent />
      </aside>
      {sidebarOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setSidebarOpen(false)} />
          <aside className={`lg:hidden flex flex-col fixed inset-y-0 left-0 z-40 bg-gradient-to-b from-slate-900 via-slate-800 to-blue-900 border-r border-blue-800/40 overflow-hidden transition-all duration-200 ${collapsed ? 'w-14' : 'w-52'}`} style={{ height: '100dvh' }}>
            <SidebarContent />
          </aside>
        </>
      )}
      <div className={`flex-1 flex flex-col min-h-screen min-w-0 transition-all duration-200 ${collapsed ? 'lg:ml-14' : 'lg:ml-52'}`} style={{ overflowY: 'auto' }}>
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between lg:hidden flex-shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="p-2">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <div className="w-6" />
        </header>
        <main className="flex-1 min-h-0 px-6 pt-3 pb-6 overscroll-contain">{children}</main>
      </div>

    </div>
  );
}